import { migrateDiceCandidate } from './candidatesApi';
import { resolveDiceProfileId } from './diceCandidateMapper';

export function normalizeSourceKey(value) {
  if (value && typeof value === 'object') {
    return normalizeSourceKey(value.value ?? value.key ?? value.id ?? value.label ?? value.name);
  }

  return String(value ?? '').trim().toLowerCase();
}

export function getSearchSources(value) {
  return (Array.isArray(value) ? value : [value])
    .flat()
    .map(normalizeSourceKey)
    .filter((item, index, source) => item && item !== 'all' && source.indexOf(item) === index);
}

export function resolveSearchSource(value, fallback = 'internal') {
  const sources = getSearchSources(value);
  if (sources.includes('internal')) return 'internal';
  const normalizedFallback = normalizeSourceKey(fallback);
  if (sources[0]) return sources[0];
  if (normalizedFallback) return normalizedFallback;
  return fallback === '' ? '' : 'internal';
}

// Which module key answers for each source tab, as { [sourceKey]: moduleKey }.
// A source with no entry uses the form's own module, which is every source on
// every form until someone configures otherwise.
//
// Two places can declare it and BOTH are honoured, config last so it wins:
//   1. a `sourceModules` prop, for a host app that wants to say so in code
//      before the option key is persisted backend-side;
//   2. the source option itself ({ value:'dice', module:'dice' }), which is what
//      the Admin option editor writes.
//
// Built once and shared, because the module has to be identical on both request
// paths — the `/module-data-list` call and the unified search endpoint. They
// each used to work it out for themselves, and only one of the two read config,
// so the Dice tab asked for Candidates' columns and actions.
export function buildSourceModuleMap(fields, sourceModules) {
  const map = { ...(sourceModules ?? {}) };

  (Array.isArray(fields) ? fields : [])
    .flatMap((field) => (Array.isArray(field?.options) ? field.options : []))
    .forEach((option) => {
      const key = normalizeSourceKey(option?.value);
      // Blank is not a value: it means "use the form's module", the same reading
      // the Admin editor gives it by refusing to store an empty module.
      const module = String(option?.module ?? '').trim();
      if (key && module) map[key] = module;
    });

  return map;
}

function toTitle(value) {
  return String(value ?? '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

// The key a tab is MATCHED by — lowercased, so a configured source and the
// backend's spelling of it land on the same map entry.
function getTabKey(tab) {
  return normalizeSourceKey(rawTabKey(tab));
}

// The key a tab is EMITTED with, which is what goes back to the API when the tab
// is clicked. It has to survive verbatim: a source key is lowercase either way,
// but a field-based tab like "myJobs" is not, and sending "myjobs" would filter
// on a value no record has.
function rawTabKey(tab) {
  const key = tab?.key ?? tab?.value ?? tab?.id ?? tab?.source ?? tab?.name ?? tab?.title ?? tab?.label;
  return String(key ?? '').trim();
}

function getTabCount(tab) {
  const count = tab?.count ?? tab?.countValue;
  const number = Number(count);
  return Number.isFinite(number) ? number : undefined;
}

function getSourceCount(source, responseCounts, currentCounts, activeSource, activeCount) {
  // Both endpoints report a count for EVERY requested source, not just the one
  // being viewed — module-data-list in `count.internal`/`count.dice`, boolean
  // search in the same shape. So the live response wins for any tab; restricting
  // this to the active source left the other tab showing a stale cached figure
  // (or none) even though the number was sitting right there in the response.
  const responseCount = Number(responseCounts?.[source]);
  if (Number.isFinite(responseCount)) return responseCount;

  if (source === activeSource) {
    const activeTotal = Number(activeCount);
    if (Number.isFinite(activeTotal)) return activeTotal;
  }

  const currentCount = currentCounts.get(source);
  if (currentCount !== undefined) return currentCount;

  return undefined;
}

export function buildSourceTabs({
  sources = [],
  activeSource = 'internal',
  activeCount = 0,
  responseCounts = {},
  currentTabs = [],
  backendTabs = [],
} = {}) {
  const currentCounts = new Map(
    (Array.isArray(currentTabs) ? currentTabs : [])
      .map((tab) => [getTabKey(tab), getTabCount(tab)])
      .filter(([source, count]) => source && count !== undefined),
  );

  const sourceMap = new Map();

  // Pass 1 — the CONFIGURED sources define the order. Seeding from backendTabs
  // instead put whichever tab was active first, because the backend only ever
  // returns a tab for the active source — so the strip reordered on every
  // switch. A Map keeps insertion order, and set() on an existing key updates
  // the value without moving it, which is what makes pass 2 order-safe.
  // Falling back to [activeSource] when nothing is configured used to invent an
  // "Internal" tab on every form, including modules with no source picker at
  // all — it would sit beside a Jobs form's real My Jobs / All Jobs tabs. With
  // no configured sources there is nothing to seed, and pass 2 lets the
  // backend's own tabs stand on their own.
  const selectedSources = getSearchSources(sources);
  const activeKey = normalizeSourceKey(activeSource);
  const seeds = selectedSources.length
    ? selectedSources
    : (activeKey ? [activeKey] : []);

  seeds.forEach((source) => {
    if (!source || sourceMap.has(source)) return;
    const count = getSourceCount(source, responseCounts, currentCounts, activeSource, activeCount);
    sourceMap.set(source, {
      key: source,
      title: toTitle(source),
      ...(count !== undefined ? { count } : {}),
    });
  });

  // Pass 2 — the backend contributes a real label and count, never a position.
  // Falling back to the previous count keeps a known figure on screen instead
  // of blanking it out the moment you switch away from that tab.
  (Array.isArray(backendTabs) ? backendTabs : []).forEach((tab) => {
    const key = getTabKey(tab);
    if (!key) return;
    const previous = sourceMap.get(key);
    const count = getTabCount(tab) ?? previous?.count ?? currentCounts.get(key);
    sourceMap.set(key, {
      // Matched case-insensitively, emitted as the backend spelled it.
      key: previous ? previous.key : rawTabKey(tab),
      title: tab.title ?? tab.label ?? tab.name ?? previous?.title ?? toTitle(key),
      ...(count !== undefined ? { count } : {}),
    });
  });

  return Array.from(sourceMap.values());
}

function collectSourceValues(record) {
  return [
    record?.sourceType,
    record?.profileSource,
    record?.selectedSource,
    record?.source,
    record?.provider,
    record?.customFields?.sourceType,
    record?.customFields?.profileSource,
    record?.customFields?.source,
    record?.customFields?.provider,
  ].flat().map(normalizeSourceKey).filter(Boolean);
}

function recordMatchesSource(record, sourceKey, marker) {
  const normalizedSource = normalizeSourceKey(sourceKey);
  if (!normalizedSource) return false;
  if (collectSourceValues(record).includes(normalizedSource)) return true;
  return typeof marker === 'function' ? marker(record) : false;
}

const SOURCE_ACTIONS = [
  {
    source: 'dice',
    field: '__sourceAction_dice_migrate',
    label: 'Action',
    buttonLabel: 'Migrate',
    completedLabel: 'Migrated',
    successMessage: 'Candidate migrated successfully.',
    errorMessage: 'Failed to migrate candidate.',
    isRecordFromSource: (record) => Boolean(record?.customFields?.diceProfileData),
    getRecordId: resolveDiceProfileId,
    execute: migrateDiceCandidate,
  },
];

export function getAvailableSourceActions({ rows = [], selectedSource = '' } = {}) {
  return SOURCE_ACTIONS.filter((action) => {
    const selectedMatches = normalizeSourceKey(selectedSource) === action.source;
    return selectedMatches || rows.some((record) => recordMatchesSource(record, action.source, action.isRecordFromSource));
  });
}

export function sourceActionAppliesToRecord(action, record, selectedSource = '') {
  return normalizeSourceKey(selectedSource) === action.source
    || recordMatchesSource(record, action.source, action.isRecordFromSource);
}

export function getSourceActionInstanceKey(action, record) {
  const id = action?.getRecordId?.(record);
  return id ? `${action.source}:${action.field}:${id}` : '';
}

export async function runSourceAction(action, record) {
  const id = action?.getRecordId?.(record);
  if (!id) throw new Error(`${action?.buttonLabel ?? 'Action'} ID is required`);
  return action.execute(id, record);
}
